// Chaves de API — a credencial que um ERP usa para falar com a plataforma.
//
// Até 26/10/2026 esta tela era um marcador do mockup. O que a trouxe à vida
// foi a integração de ERP: listar domínios, consumo, criar caixa, trocar
// senha, cota e suspensão — tudo isso a API já fazia, e faltava só a
// credencial de máquina e um lugar para criá-la.
//
// A TELA GIRA EM TORNO DE UM FATO: a chave aparece UMA VEZ. O banco guarda
// só o hash, e é esse contrato que faz um vazamento do banco não virar
// acesso. Por isso o momento da criação tem tratamento próprio, com a chave
// em destaque, um botão de copiar e um aviso que não dá para ignorar.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { chavesApi, revendas, type ChaveApi } from "../api/admin";
import { explicar } from "../api/problema";
import { Icone } from "../ui/Icone";
import { Botao, Cabecalho, Celula, ConfirmarRemocao, Estado, Linha, Selo, Tabela } from "./comuns";

function quando(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export function Integracoes() {
  const fila = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [nova, setNova] = useState<{ nivel: "revenda" | "super_admin"; revenda_uuid: string; nome: string }>(
    { nivel: "revenda", revenda_uuid: "", nome: "" },
  );
  const [recemCriada, setRecemCriada] = useState<{ nome: string; chave: string; nivel: string } | null>(null);
  const [aRevogar, setARevogar] = useState<ChaveApi | null>(null);
  const [copiada, setCopiada] = useState(false);
  const [erro, setErro] = useState("");

  const lista = useQuery({
    queryKey: ["p", "chaves-api"],
    queryFn: chavesApi.listar,
  });

  const asRevendas = useQuery({
    queryKey: ["p", "revendas", "para-chave"],
    queryFn: () => revendas.listar({ por_pagina: 200 }),
  });

  const criar = useMutation({
    mutationFn: () => chavesApi.criar({ ...nova, nome: nova.nome.trim() }),
    onSuccess: (r) => {
      setErro("");
      setCriando(false);
      setNova({ nivel: "revenda", revenda_uuid: "", nome: "" });
      setCopiada(false);
      // A CHAVE FICA NA TELA ATÉ A PESSOA FECHAR. Nada de aviso que some
      // sozinho: quem perder este momento não recupera a chave de jeito
      // nenhum, e teria que revogar e criar outra.
      setRecemCriada({ nome: r.nome, chave: r.chave, nivel: r.nivel });
      void fila.invalidateQueries({ queryKey: ["p", "chaves-api"] });
    },
    onError: (e) => setErro(explicar(e)),
  });

  const revogar = useMutation({
    mutationFn: (c: ChaveApi) => chavesApi.revogar(c.uuid),
    onSuccess: () => {
      setARevogar(null);
      void fila.invalidateQueries({ queryKey: ["p", "chaves-api"] });
    },
    onError: (e) => setErro(explicar(e)),
  });

  const itens = lista.data ?? [];

  return (
    <>
      <Cabecalho
        titulo="API e integrações"
        descricao="Chaves para um ERP, um sistema ou um agente falar com a plataforma. A chave de revenda alcança só os domínios dela; a de super admin, todas as revendas."
        acao={
          <Botao icone="KeyRound" aoClicar={() => { setErro(""); setCriando((c) => !c); }}>
            Nova chave
          </Botao>
        }
      />

      {/* A CHAVE RECÉM-CRIADA. Ocupa o topo da tela e não sai sozinha. */}
      {recemCriada && (
        <div className="mb-5 rounded-xl border border-acao/40 bg-acao-clara p-4">
          <p className="text-sm font-medium text-tinta">
            Chave de “{recemCriada.nome}” criada.
          </p>
          <p className="mt-1 text-xs text-tinta-media">
            <strong>Guarde agora.</strong> Ela não será mostrada de novo — o servidor
            guarda só um resumo dela. Quem perder, revoga e cria outra.
          </p>
          {recemCriada.nivel === "super_admin" && (
            <p className="mt-1 text-xs text-tinta-media">
              Para o agente: endereço <code>/mcp</code> do webmail da plataforma, com o
              cabeçalho <code>Authorization: Bearer</code> seguido desta chave.
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 overflow-x-auto rounded-lg bg-white px-3 py-2 font-mono text-xs text-tinta">
              {recemCriada.chave}
            </code>
            <button
              onClick={() => {
                void navigator.clipboard?.writeText(recemCriada.chave);
                setCopiada(true);
              }}
              className="rounded-lg bg-acao px-3 py-2 text-xs font-medium text-white hover:bg-acao-escura"
            >
              {copiada ? "copiada" : "copiar"}
            </button>
            <button
              onClick={() => setRecemCriada(null)}
              className="rounded-lg px-3 py-2 text-xs text-tinta-media hover:bg-white/60"
            >
              já guardei
            </button>
          </div>
        </div>
      )}

      {criando && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            criar.mutate();
          }}
          className="mb-5 flex flex-wrap items-end gap-2 rounded-xl border border-linha bg-fundo p-4"
        >
          <label className="min-w-[200px] flex-1 text-xs text-tinta-media">
            Alcance
            <select
              value={nova.nivel}
              onChange={(e) =>
                setNova((n) => ({ ...n, nivel: e.target.value as "revenda" | "super_admin" }))
              }
              className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm text-tinta"
            >
              <option value="revenda">Uma revenda — só os domínios dela</option>
              <option value="super_admin">Todas as revendas (super admin)</option>
            </select>
          </label>
          {nova.nivel === "revenda" && (
            <label className="min-w-[200px] flex-1 text-xs text-tinta-media">
              De qual revenda?
              <select
                value={nova.revenda_uuid}
                onChange={(e) => setNova((n) => ({ ...n, revenda_uuid: e.target.value }))}
                required
                className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm text-tinta"
              >
                <option value="">escolha…</option>
                {asRevendas.data?.itens?.map((r) => (
                  <option key={r.uuid} value={r.uuid}>{r.nome}</option>
                ))}
              </select>
            </label>
          )}
          {/* O QUE A CHAVE DE SUPER ADMIN FAZ, ANTES DE CRIAR. É a chave
              mais poderosa que a plataforma emite, e quem a cria precisa
              ler o alcance dela no momento da decisão, não depois. */}
          {nova.nivel === "super_admin" && (
            <p className="w-full rounded-lg border border-aviso/40 bg-aviso/10 px-3 py-2 text-xs text-tinta">
              <strong>Alcança todas as revendas e todos os clientes</strong>: consulta e altera
              como o super admin no painel — para o agente que faz o suporte das revendas.
              <span className="text-tinta-media">
                {" "}Duas coisas nenhuma chave faz: ler o e-mail de alguém e criar ou revogar
                chaves. Se ela vazar, revogue aqui — ela para de funcionar na hora.
              </span>
            </p>
          )}
          <label className="min-w-[240px] flex-1 text-xs text-tinta-media">
            Para que serve
            <input
              value={nova.nome}
              onChange={(e) => setNova((n) => ({ ...n, nome: e.target.value }))}
              placeholder="ERP financeiro"
              maxLength={80}
              required
              className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm text-tinta"
            />
            {/* O NOME É OBRIGATÓRIO, e vale dizer por quê: três chaves sem
                nome viram três enigmas na hora de revogar uma delas. */}
            <span className="mt-1 block text-[11px] text-tinta-fraca">
              É por este nome que você vai saber qual revogar, daqui a seis meses.
            </span>
          </label>
          <Botao tipo="submit" ocupado={criar.isPending} icone="KeyRound">
            Criar chave
          </Botao>
        </form>
      )}

      {erro && (
        <p className="mb-4 text-sm text-perigo" role="alert">{erro}</p>
      )}

      <Estado consulta={lista} vazio={itens.length === 0}>
        <Tabela colunas={["Nome", "Identificador", "Revenda", "Criada", "Último uso", "", ""]}>
          {itens.map((c) => (
            <Linha key={c.uuid}>
              <Celula>
                <span className="font-medium text-tinta">{c.nome}</span>
              </Celula>
              <Celula>
                {/* O PREFIXO, e nunca a chave. É a parte pública: identifica
                    qual é sem revelar o segredo. */}
                <code className="font-mono text-xs text-tinta-media">{c.prefixo}…</code>
              </Celula>
              <Celula>
                {c.nivel === "super_admin" ? (
                  <Selo texto="todas (super admin)" tom="aviso" />
                ) : (
                  c.revenda
                )}
              </Celula>
              <Celula>
                <span className="text-xs">{quando(c.criado_em)}</span>
                <span className="block text-[11px] text-tinta-fraca">por {c.criada_por}</span>
              </Celula>
              <Celula>
                {/* "NUNCA USADA" é informação, não ausência de informação:
                    é o que diz que a integração ainda não foi ligada — ou
                    que esta chave está sobrando. */}
                {c.usada_em ? (
                  <span className="text-xs">{quando(c.usada_em)}</span>
                ) : (
                  <span className="text-xs text-tinta-fraca">nunca usada</span>
                )}
              </Celula>
              <Celula>
                <Selo texto={c.ativa ? "ativa" : "revogada"} tom={c.ativa ? "ok" : "neutro"} />
              </Celula>
              <Celula>
                {c.ativa && (
                  <button
                    onClick={() => { revogar.reset(); setARevogar(c); }}
                    className="text-xs text-perigo underline"
                  >
                    revogar
                  </button>
                )}
              </Celula>
            </Linha>
          ))}
        </Tabela>
      </Estado>

      {aRevogar && (
        <ConfirmarRemocao
          titulo="Revogar chave"
          frase={aRevogar.nome}
          rotulo="Revogar"
          ocupado={revogar.isPending}
          erro={revogar.error ? explicar(revogar.error) : undefined}
          aoCancelar={() => setARevogar(null)}
          aoConfirmar={() => revogar.mutate(aRevogar)}
          oQueAcontece={
            <>
              <p>
                A chave <strong>{aRevogar.nome}</strong> para de funcionar na
                próxima chamada. Não há como desfazer: para voltar a integrar,
                crie outra e troque no sistema que a usa.
              </p>
              <p className="text-tinta-fraca">
                O registro fica, com quem revogou e quando — revogação que some
                do histórico é revogação que ninguém consegue auditar.
              </p>
            </>
          }
        />
      )}

      {/* O QUE A CHAVE NÃO PODE, na tela e não só na documentação.
          Quem cria a chave precisa saber o limite dela ANTES de entregá-la a
          um fornecedor — descobrir pelo 403 é descobrir tarde. */}
      <div className="mt-8 rounded-xl border border-linha p-4">
        <h2 className="flex items-center gap-2 text-sm font-medium text-tinta">
          <Icone nome="ShieldCheck" tamanho={16} className="text-acao" />
          O que uma chave pode, e o que não pode
        </h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <ul className="space-y-1.5 text-sm text-tinta-media">
            <li>Listar domínios, com o consumo de cada um</li>
            <li>Listar, criar e apagar caixas</li>
            <li>Trocar senha, mudar cota, suspender</li>
            <li>Suspender domínio</li>
          </ul>
          <ul className="space-y-1.5 text-sm text-tinta-media">
            <li className="text-perigo">Não entra na caixa do cliente nem lê e-mail</li>
            <li className="text-perigo">Não cria nem lista outras chaves</li>
            <li className="text-perigo">Não alcança domínio de outra revenda</li>
          </ul>
        </div>
        {/* ENDEREÇO, não caminho de arquivo. Antes esta linha apontava para
            `docs/api-integracao.md`, que é onde o arquivo mora no
            repositório — inútil para quem está nesta tela e precisa MANDAR a
            documentação a um desenvolvedor de fora. As duas páginas são
            públicas, servidas por este mesmo servidor. */}
        <p className="mt-3 text-xs text-tinta-fraca">
          Para entregar a quem vai integrar:{" "}
          <a href="/docs/api" target="_blank" rel="noreferrer" className="text-acao underline">
            referência das rotas
          </a>{" "}
          (interativa, dá para testar com a chave) e{" "}
          <a href="/docs/integracao" target="_blank" rel="noreferrer" className="text-acao underline">
            guia de integração
          </a>
          .
        </p>
      </div>
    </>
  );
}
